import { useState, useEffect } from 'react';
import { Building2, Users, FileText, Loader2, TreePine, Mail, ClipboardList, FolderOpen, Phone, Globe, MapPin, User } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

const API_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

function authHeaders() {
  return { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('olympus_token')}` };
}

function StatBadge({ count, label, color = 'slate' }) {
  if (!count) return null;
  const colors = { blue: 'bg-blue-100 text-blue-700', green: 'bg-emerald-100 text-emerald-700', amber: 'bg-amber-100 text-amber-700', slate: 'bg-slate-100 text-slate-700', violet: 'bg-violet-100 text-violet-700' };
  return <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${colors[color] || colors.slate}`}>{count} {label}</span>;
}

function Section({ icon: Icon, title, count, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Card className="border-slate-200">
      <CardHeader className="py-2 px-3 cursor-pointer hover:bg-slate-50" onClick={() => setOpen(!open)}>
        <CardTitle className="text-xs flex items-center gap-2 font-medium">
          <Icon className="w-3.5 h-3.5 text-slate-500" />
          {title}
          <Badge variant="secondary" className="text-[10px] ml-auto">{count}</Badge>
        </CardTitle>
      </CardHeader>
      {open && <CardContent className="px-3 py-2 space-y-1 max-h-52 overflow-y-auto">{children}</CardContent>}
    </Card>
  );
}

export function InstitutionDetail({ institution }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!institution) return;
    setLoading(true);
    fetch(`${API_URL}/api/institutionen/${institution.id}/netzwerk`, { headers: authHeaders() })
      .then(r => r.json())
      .then(d => setData(d))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [institution]);

  if (!institution) return <div className="p-8 text-center text-slate-400 text-sm" data-testid="institution-detail-empty">Institution auswählen</div>;

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;

  if (!data || data.fehler) return <div className="p-4 text-red-500 text-sm">{data?.fehler || 'Fehler beim Laden'}</div>;

  const inst = data.institution || {};
  const stats = data.statistik || {};

  return (
    <div className="p-4 space-y-3 overflow-y-auto max-h-[calc(100vh-200px)]" data-testid="institution-detail">
      {/* Header */}
      <div>
        <h2 className="text-base font-bold text-slate-800">{inst.name}</h2>
        {inst.abteilung && <p className="text-xs text-blue-600">{inst.abteilung}</p>}
        <div className="flex gap-1.5 mt-2 flex-wrap">
          {inst.typ && inst.typ !== 'sonstiges' && <Badge variant="outline" className="text-[10px]">{inst.typ}</Badge>}
          {inst.ort && <Badge variant="secondary" className="text-[10px]"><MapPin className="w-2.5 h-2.5 mr-0.5 inline" />{inst.ort}</Badge>}
        </div>
        {/* Kontaktdaten */}
        <div className="mt-2 space-y-0.5 text-xs text-slate-600">
          {inst.strasse && <p><MapPin className="w-3 h-3 inline mr-1 text-slate-400" />{inst.strasse}{inst.plz ? `, ${inst.plz} ${inst.ort || ''}` : ''}</p>}
          {inst.telefon && <p><Phone className="w-3 h-3 inline mr-1 text-slate-400" />{inst.telefon}</p>}
          {inst.email && <p><Mail className="w-3 h-3 inline mr-1 text-slate-400" />{inst.email}</p>}
          {inst.webseite && <p><Globe className="w-3 h-3 inline mr-1 text-slate-400" /><a href={inst.webseite} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">{inst.webseite}</a></p>}
        </div>
        {/* Statistik-Zeile */}
        <div className="flex gap-1.5 mt-2 flex-wrap">
          <StatBadge count={stats.betreute} label="Betreute" color="blue" />
          <StatBadge count={stats.ansprechpartner} label="Personen" color="green" />
          <StatBadge count={stats.dokumente} label="Dokumente" color="amber" />
          <StatBadge count={stats.aufgaben} label="Aufgaben" color="violet" />
          <StatBadge count={stats.korrespondenz} label="Briefe" color="slate" />
          <StatBadge count={stats.vorgaenge} label="Vorgänge" color="blue" />
          <StatBadge count={stats.abteilungen} label="Abteilungen" color="green" />
        </div>
      </div>

      {/* Abteilungen */}
      {data.abteilungen?.length > 0 && (
        <Section icon={TreePine} title="Abteilungen" count={data.abteilungen.length}>
          {data.abteilungen.map(c => (
            <div key={c.id} className="text-xs flex items-center gap-1.5 py-0.5 text-slate-700">
              <Building2 className="w-3 h-3 text-blue-500" /> {c.name}
            </div>
          ))}
        </Section>
      )}

      {/* Betreute */}
      <Section icon={Users} title="Betreute" count={data.betreute?.length || 0} defaultOpen={true}>
        {!data.betreute?.length ? <p className="text-[11px] text-slate-400">Keine Betreuten verknüpft</p> : data.betreute.map(k => (
          <div key={k.id} className="text-xs flex items-center justify-between py-0.5">
            <span className="text-slate-700 font-medium">{k.name}</span>
            <div className="flex gap-1">
              {k.aktenzeichen && <Badge variant="outline" className="text-[9px] px-1">{k.aktenzeichen}</Badge>}
              {k.sachbearbeiter && <Badge variant="secondary" className="text-[9px] px-1">{k.sachbearbeiter}</Badge>}
              {k.versicherungsnummer && <Badge variant="secondary" className="text-[9px] px-1">{k.versicherungsnummer}</Badge>}
              {k.status && <Badge className={`text-[9px] px-1 ${k.status === 'aktiv' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>{k.status}</Badge>}
            </div>
          </div>
        ))}
      </Section>

      {/* Ansprechpartner */}
      <Section icon={User} title="Ansprechpartner" count={data.ansprechpartner?.length || 0}>
        {!data.ansprechpartner?.length ? <p className="text-[11px] text-slate-400">Keine Ansprechpartner</p> : data.ansprechpartner.map(p => (
          <div key={p.id} className="text-xs flex items-center justify-between py-0.5">
            <span className="text-slate-700">{p.nachname || p.name}{p.vorname ? `, ${p.vorname}` : ''}</span>
            <div className="flex gap-1">
              {(p.funktion || p.rolle) && <Badge variant="outline" className="text-[9px] px-1">{p.funktion || p.rolle}</Badge>}
              {p.telefon && <span className="text-[10px] text-slate-400">{p.telefon}</span>}
              {p.email && <span className="text-[10px] text-blue-500">{p.email}</span>}
            </div>
          </div>
        ))}
      </Section>

      {/* Dokumente */}
      {(data.dokumente?.length > 0) && (
        <Section icon={FileText} title="Dokumente" count={data.dokumente.length} defaultOpen={false}>
          {data.dokumente.map(d => (
            <div key={d.id} className="text-xs flex items-center justify-between py-0.5">
              <span className="text-slate-700 truncate max-w-[200px]">{d.titel || 'Unbenannt'}</span>
              <div className="flex gap-1 flex-shrink-0">
                {d.klient_name && <span className="text-[10px] text-slate-400">{d.klient_name}</span>}
                {d.kategorie && <Badge variant="outline" className="text-[9px] px-1">{d.kategorie}</Badge>}
              </div>
            </div>
          ))}
        </Section>
      )}

      {/* Aufgaben */}
      {(data.aufgaben?.length > 0) && (
        <Section icon={ClipboardList} title="Aufgaben" count={data.aufgaben.length} defaultOpen={false}>
          {data.aufgaben.map(a => (
            <div key={a.id} className="text-xs flex items-center justify-between py-0.5">
              <span className="text-slate-700 truncate max-w-[200px]">{a.titel}</span>
              <div className="flex gap-1 flex-shrink-0">
                {a.klient_name && <span className="text-[10px] text-slate-400">{a.klient_name}</span>}
                <Badge className={`text-[9px] px-1 ${a.status === 'offen' ? 'bg-amber-100 text-amber-700' : a.status === 'erledigt' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>{a.status}</Badge>
              </div>
            </div>
          ))}
        </Section>
      )}

      {/* Korrespondenz */}
      {(data.korrespondenz?.length > 0) && (
        <Section icon={Mail} title="Korrespondenz" count={data.korrespondenz.length} defaultOpen={false}>
          {data.korrespondenz.map(k => (
            <div key={k.id} className="text-xs flex items-center justify-between py-0.5">
              <span className="text-slate-700 truncate max-w-[200px]">{k.betreff || 'Kein Betreff'}</span>
              <div className="flex gap-1 flex-shrink-0">
                {k.klient_name && <span className="text-[10px] text-slate-400">{k.klient_name}</span>}
                {k.kanal && <Badge variant="outline" className="text-[9px] px-1">{k.kanal}</Badge>}
              </div>
            </div>
          ))}
        </Section>
      )}

      {/* Vorgänge */}
      {(data.vorgaenge?.length > 0) && (
        <Section icon={FolderOpen} title="Vorgänge" count={data.vorgaenge.length} defaultOpen={false}>
          {data.vorgaenge.map(v => (
            <div key={v.id} className="text-xs flex items-center justify-between py-0.5">
              <span className="text-slate-700 truncate max-w-[200px]">{v.titel}</span>
              <div className="flex gap-1 flex-shrink-0">
                {v.klient_name && <span className="text-[10px] text-slate-400">{v.klient_name}</span>}
                {v.kategorie && <Badge variant="outline" className="text-[9px] px-1">{v.kategorie}</Badge>}
              </div>
            </div>
          ))}
        </Section>
      )}
    </div>
  );
}
